import DOMPurify from 'dompurify';

const correspondenceAllowedTags = [
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
] as const;

const correspondenceAllowedAttributes = [
  'align',
  'colspan',
  'href',
  'rel',
  'rowspan',
  'target',
  'title',
] as const;

const allowedHrefProtocols = ['http:', 'https:', 'mailto:', 'tel:'] as const;

export const sanitizeCorrespondenceHtml = (html?: string | null) => {
  const sanitizedHtml = DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS: [...correspondenceAllowedTags],
    ALLOWED_ATTR: [...correspondenceAllowedAttributes],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: [
      'base',
      'embed',
      'form',
      'iframe',
      'img',
      'input',
      'link',
      'meta',
      'object',
      'script',
      'style',
    ],
    FORBID_ATTR: ['srcset', 'style'],
    USE_PROFILES: { html: true },
  });

  if (typeof document === 'undefined') {
    return sanitizedHtml;
  }

  const template = document.createElement('template');
  template.innerHTML = sanitizedHtml;

  template.content.querySelectorAll('a').forEach(anchor => {
    const href = anchor.getAttribute('href');

    if (!href) {
      anchor.removeAttribute('target');
      anchor.removeAttribute('rel');
      return;
    }

    const normalizedHref = href.trim();

    if (normalizedHref.startsWith('/')) {
      anchor.setAttribute('rel', 'noopener noreferrer nofollow');
      return;
    }

    try {
      const parsedUrl = new URL(normalizedHref, window.location.origin);

      if (
        !allowedHrefProtocols.includes(parsedUrl.protocol as (typeof allowedHrefProtocols)[number])
      ) {
        anchor.removeAttribute('href');
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
        return;
      }
    } catch {
      anchor.removeAttribute('href');
      anchor.removeAttribute('target');
      anchor.removeAttribute('rel');
      return;
    }

    anchor.setAttribute('rel', 'noopener noreferrer nofollow');
  });

  return template.innerHTML;
};
